/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './site-group-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-site-group-edit-page',
  template
})
export class SiteGroupEditPageComponent extends AdminResourceEditPageBase {
  type: 'NETWORK' | 'TEAM' = 'NETWORK';
  teams: any[] | null = null;
  mpTeams: any[] = [];
  allMarketplacePublisherTeams: any[] = [];
  showOwnership = false;
  hasNetworkPermission = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    @Inject(TeamResource) private Team: any,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.hasNetworkPermission = LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM');
    this.deferFormDirtyTracking();

    this.Team.query().$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        const allTeams = page.results;
        const isViewAllObjects = LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        this.teams = isViewAllObjects ? allTeams : _.filter(allTeams, (team: any) => {
          return LocalUserPermissions.isPartOf(team.network.tagId || team.network.id, team.id);
        });
        this.mpTeams = _.filter(this.teams, (team: any) => team.type !== 'AX_PUBLISHER');
        this.allMarketplacePublisherTeams = _.filter(this.teams, (team: any) => team.type === 'AX_PUBLISHER');
        this.showOwnership = this.allMarketplacePublisherTeams.length > 0;
        this.resolveOwnerTeamReference();
        this.resumeFormDirtyTracking();
      });
    });
  }

  protected afterInitEditingModel(): void {
    this.editingModel.teams = this.normalizeTeamIds(this.editingModel.teams);
    this.type = this.editingModel.ownerTeam ? 'TEAM' : 'NETWORK';
    this.resolveOwnerTeamReference();
  }

  protected afterInitAfterSave(): void {
    this.type = this.editingModel.ownerTeam ? 'TEAM' : 'NETWORK';
    this.resolveOwnerTeamReference();
  }

  protected onBeforeSubmit(): void {
    if (this.editingModel.ownerTeam) {
      const validMpTeams = _.map(this.mpTeams, 'id');
      this.editingModel.teams = _.filter(this.editingModel.teams, (teamId: string) => {
        return validMpTeams.indexOf(teamId) > -1;
      });
    }
    if (this.type === 'NETWORK' || !this.editingModel.ownerTeam) {
      this.editingModel.ownerTeam = null;
      this.editingModel.teams = [];
    }
  }

  onTypeChange(): void {
    if (this.type === 'NETWORK') {
      this.editingModel.ownerTeam = null;
      this.editingModel.teams = [];
    }
    this.markExternalFormDirty();
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.mpTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);

    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }

    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  private normalizeTeamIds(teams: any): string[] {
    return _.map(teams || [], (team: any) => _.isString(team) ? team : team.id);
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.allMarketplacePublisherTeams.length) {
      return;
    }
    const ownerTeamId = _.isString(this.editingModel.ownerTeam)
      ? this.editingModel.ownerTeam
      : this.editingModel.ownerTeam.id;
    const match = _.find(this.allMarketplacePublisherTeams, ['id', ownerTeamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }
}
